<template>
    <div id="config-objects">
        <FilterView placeholder="Chercher un groupe d'objets..." v-model="searchTerm">
            <CustomButton
                v-if="selected.length"
                icon="trash-alt"
                class="default red-theme"
                @click="showConfirmMany = true"
            >
                Supprimer
            </CustomButton>
            <CustomButton icon="plus" class="default green-theme" @click="handleNew">
                Ajouter un groupe d'objets
            </CustomButton>
        </FilterView>
        <Modal
            v-if="showEdit"
            v-model="object"
            :themes="themes"
            :keys="keys"
            @dismiss="showEdit = false"
            @close="handleClose"
        />
        <Confirm v-if="showConfirmOne" @dismiss="showConfirmOne = false" @close="handleDeleteOne">
            <span slot="header">Supprimer le groupe d'objets</span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer ce groupe d'objets ?<br />
                Il ne sera plus disponible à la validation.
            </span>
        </Confirm>
        <Confirm
            v-if="showConfirmMany"
            @dismiss="showConfirmMany = false"
            @close="handleDeleteMany"
        >
            <span slot="header">
                Supprimer {{ selected.length }}
                {{ selected.length > 1 ? 'groupes' : 'groupe' }} d'objets
            </span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer les groupes d'objets sélectionnés ?<br />
                Ils ne seront plus disponibles à la validation.
            </span>
        </Confirm>
        <h2 class="title">Groupes d'objets</h2>
        <LoadingDots v-if="isLoading" />
        <CardPager>
            <ObjectCard
                v-for="item in filtered"
                :item="item"
                :key="item.id"
                :handleEdit="handleEdit"
                :handleConfirm="handleConfirm"
                :handleDisable="handleDisable"
                :handleAuto="handleAuto"
                :selected="selected"
                @selected="handleSelect"
            />
        </CardPager>
    </div>
</template>

<script>
import FilterView from '@/components/config/Filter';
import Modal from '@/components/config/ModalObject';
import LoadingDots from '@/components/LoadingDots';
import ObjectCard from '@/components/config/ObjectCard';
import CardPager from '@/components/CardPager';
import CustomButton from '@/components/Button';
import Confirm from '@/components/config/DeleteConfirm';

export default {
    data() {
        return {
            showEdit: false,
            showConfirmOne: false,
            showConfirmMany: false,
            themes: [],
            selected: [],
            objects: [],
            object: null,
            keys: [],
            searchTerm: null,
            isLoading: true,
        };
    },
    watch: {
        searchTerm() {
            this.selected.splice(0);
        },
    },
    computed: {
        filtered() {
            if (this.searchTerm) {
                const re = new RegExp(this.searchTerm, 'i');
                return this.objects.filter((o) => re.test(o.name));
            }
            return this.objects;
        },
    },
    mounted() {
        Promise.all([
            this.$http.get('/api/object-group'),
            this.$http.get('/api/theme'),
            this.$http.get('/api/tag'),
        ]).then(
            (responses) => {
                // success callback
                this.objects = responses[0].body;
                this.themes = responses[1].body.map((t) => ({
                    id: t.id,
                    text: t.name,
                }));

                // tags avec traduction
                this.keys = responses[2].body
                    .filter((t) => t.name_fr != '' && t.name_fr != null)
                    .map((t) => ({ id: t.id, text: `${t.name} (${t.name_fr})` }));
                // tags sans traduction
                this.keysBis = responses[2].body
                    .filter((t) => t.name_fr == '' || t.name_fr == null)
                    .map((t) => ({ id: t.id, text: `${t.name}` }));
                // fusion des deux arrays
                this.keys.push(...this.keysBis);
                // tri du array final
                Array.prototype.sortOn = function(key) {
                    this.sort(function(a, b) {
                        if (a[key].toLowerCase() < b[key].toLowerCase()) {
                            return -1;
                        } else if (a[key].toLowerCase() > b[key].toLowerCase()) {
                            return 1;
                        }
                        return 0;
                    });
                };
                this.keys.sortOn('text');

                this.isLoading = false;
            },
            (responses) => {
                // error callback
                console.log(JSON.stringify(responses));
                this.isLoading = false;
            },
        );
    },
    methods: {
        _serializeObject(obj) {
            if (!obj) {
                return null;
            }
            return obj;
        },
        _deserializeObject(obj) {
            if (!obj) {
                return null;
            }

            // useful
            // normalize theme
            if (obj.theme) {
                obj.theme.text = obj.theme.name;
            }

            return {
                ...obj,
                theme: obj.theme || {},
                keys: obj.keys.map((k) => ({
                    ...k,
                    text: `${k.name} (${k.name_fr})`,
                })),
                query: obj.query || {},
            };
        },
        _getObject(id) {
            return this._deserializeObject(this.objects.find((o) => o.id === id));
        },
        handleSelect(event) {
            const { id, checked } = event;
            const index = this.selected.findIndex((s) => s === id);
            if (checked) {
                if (index === -1) {
                    this.selected.push(id);
                }
            } else {
                if (index > -1) {
                    this.selected.splice(index, 1);
                }
            }
        },
        handleConfirm(item) {
            this.object = this._getObject(item);
            this.showConfirmOne = true;
        },
        handleEdit(item) {
            this.object = this._getObject(item);
            this.showEdit = true;
        },

        // Bouton toggle qui active/désactive le groupe (passe "active" à false s'il est à true et inversement)
        handleDisable(item) {
            this.object = this._getObject(item);
            const obj = this._serializeObject(this.object);

            if (obj.active === true) {
                obj['active'] = false;
            } else if (obj.active === false) {
                obj['active'] = true;
            }

            this.$http
                .put(`/api/object-group/${obj.id}`, obj)
                .then((response) => {
                    const object = response.body;
                    const index = this.objects.findIndex((o) => o.id === object.id);
                    this.objects.splice(index, 1, object);
                    this.showEdit = false;
                    this.object = null;
                })
                .catch();
        },

        // Bouton toggle qui active/désactive la validation auto du groupe (passe "auto" à false s'il est à true et inversement)
        handleAuto(item) {
            this.object = this._getObject(item);
            const obj = this._serializeObject(this.object);

            if (obj.auto === true) {
                obj['auto'] = false;
            } else if (obj.auto === false) {
                obj['auto'] = true;
            }

            this.$http
                .put(`/api/object-group/${obj.id}`, obj)
                .then((response) => {
                    const object = response.body;
                    const index = this.objects.findIndex((o) => o.id === object.id);
                    this.objects.splice(index, 1, object);
                    this.showEdit = false;
                    this.object = null;
                })
                .catch();
        },

        handleClose() {
            const obj = this._serializeObject(this.object);
            // Crée le nouveau groupe d'objets, si le nom du groupe d'objets est renseigné + requête + thème
            if (!obj.id && obj.name && obj.theme.text) {
                this.$http
                    .post('/api/object-group', this.object)
                    .then((response) => {
                        const object = response.body;
                        this.objects.push(object);
                        this.showEdit = false;
                        this.object = null;
                    })
                    .catch();
                // Met à jour le groupe d'objets, si le nom du groupe d'objets est renseigné + requête + thème
            } else if (obj.id && obj.name && obj.theme.text) {
                this.$http
                    .put(`/api/object-group/${obj.id}`, obj)
                    .then((response) => {
                        const object = response.body;
                        const index = this.objects.findIndex((o) => o.id === object.id);
                        this.objects.splice(index, 1, object);
                        this.showEdit = false;
                        this.object = null;
                    })
                    .catch();
            }
        },
        handleNew() {
            this.object = {
                id: null,
                name: '',
                theme: {},
                keys: [],
                query: {},
            };
            this.showEdit = true;
        },
        handleDeleteOne() {
            this.$http
                .delete(`/api/object-group/${this.object.id}`)
                .then(() => {
                    const index = this.objects.findIndex((o) => o.id === this.object.id);
                    this.objects.splice(index, 1);
                    this.showConfirmOne = false;
                })
                .catch();
        },
        handleDeleteMany() {
            const requests = this.selected.map((s) => this.$http.delete(`/api/object-group/${s}`));
            Promise.all(requests)
                .then(() => {
                    this.showConfirmMany = false;
                    this.selected.splice(0);
                    this.isLoading = true;
                    return this.$http.get('/api/object-group');
                })
                .then((response) => {
                    // success callback
                    this.objects = response.body;
                    this.isLoading = false;
                })
                .catch(() => {
                    this.isLoading = false;
                });
        },
    },
    components: {
        Modal,
        FilterView,
        LoadingDots,
        ObjectCard,
        CardPager,
        CustomButton,
        Confirm,
    },
};
</script>

<style lang="scss"></style>
